import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Star, ShieldCheck } from "@phosphor-icons/react";

/**
 * "First Cohort" panel — replaces fabricated testimonials for a 2024-launch academy.
 * Positions authenticity as a feature: personally taught, small cohorts, verified graduates only.
 */
export default function Testimonials() {
  return (
    <div className="surface-card p-10 md:p-14 relative overflow-hidden" data-testid="first-cohort-panel">
      <ShieldCheck
        size={140}
        weight="duotone"
        className="text-[#FFD300]/10 absolute -top-8 -right-8"
        aria-hidden
      />

      <div className="relative">
        <div className="overline mb-4 flex items-center gap-3">
          <span className="w-10 h-px bg-[#FFD300]" />
          FIRST COHORT · EST. 2024
        </div>

        <h3 className="font-heading text-3xl md:text-4xl tracking-tight leading-tight mb-6 max-w-2xl">
          We only publish testimonials from <span className="text-[#FFD300]">verified graduates</span>.
        </h3>

        <p className="text-neutral-300 text-base leading-relaxed mb-8 max-w-2xl">
          Secrecy Executive Training Academy launched in 2024. Our first cohorts are training now —
          and every quote you'll ever see on this site will come from a real, verified graduate.
          No stock quotes, no marketing scripts, no fabrications.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
          {[
            { title: "Personally taught",  desc: "Every class led by an active law-enforcement officer — S. Williams or Derek Kinard, never junior staff." },
            { title: "Small cohorts",       desc: "8–20 students per class so no one gets lost at the back of the range." },
            { title: "Verified outcomes",   desc: "Certificates issued with each graduate's application-ready packet." },
          ].map((p) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
              className="border-l-2 border-[#FFD300] pl-4"
              data-testid={`first-cohort-point-${p.title.replace(/\s/g, '-').toLowerCase()}`}
            >
              <div className="font-heading text-lg tracking-tight mb-2">{p.title}</div>
              <div className="text-neutral-400 text-sm leading-relaxed">{p.desc}</div>
            </motion.div>
          ))}
        </div>

        <div className="flex items-center gap-1 mb-4">
          {[0, 1, 2, 3, 4].map((i) => (
            <Star key={i} weight="fill" size={16} className="text-[#F5D033]" />
          ))}
          <span className="ml-2 font-mono text-[11px] tracking-[0.15em] uppercase text-neutral-400">
            The standard we hold ourselves to
          </span>
        </div>

        <div className="flex flex-wrap gap-3 pt-2">
          <Link
            to="/instructor"
            data-testid="first-cohort-meet-instructor"
            className="inline-flex items-center gap-2 bg-[#FFD300] text-black font-mono text-xs font-bold tracking-[0.18em] uppercase px-5 py-3 hover:bg-[#FFE149]"
          >
            Meet the Instructors <ArrowRight size={14} weight="bold" />
          </Link>
          <Link
            to="/training"
            data-testid="first-cohort-enroll"
            className="inline-flex items-center gap-2 border border-white/20 text-white font-mono text-xs font-bold tracking-[0.18em] uppercase px-5 py-3 hover:bg-white/10"
          >
            Join a First Cohort <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </div>
    </div>
  );
}
